import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { recordJourneyExit, recordJourneyInteraction, recordJourneyVisibility } from "./journeyTracking";

/** Shared web/PWA visibility plus native iOS app-state signals. No input contents are collected. */
export function watchJourneyLifecycle(current: () => { uid?: string; pathname: string; search: string }): () => void {
  let nativeActive = true;
  let disposed = false;
  let nativeEventReceived = false;
  let lastEdit = -Infinity;
  let removeNative: (() => Promise<void>) | undefined;
  const syncVisibility = () => {
    const { uid, pathname, search } = current();
    recordJourneyVisibility(nativeActive && document.visibilityState !== "hidden", uid, pathname, search);
  };
  const onPageHide = () => recordJourneyExit();
  const onPageShow = (event: PageTransitionEvent) => {
    if (!event.persisted) return;
    // pagehide closed the visit, including when restored from the back/forward cache.
    const { uid, pathname, search } = current();
    recordJourneyVisibility(false);
    recordJourneyVisibility(nativeActive && document.visibilityState !== "hidden", uid, pathname, search);
  };
  const onInteraction = (event: Event) => {
    if (!event.isTrusted) return;
    if (event.type === "keydown") {
      const key = event as KeyboardEvent;
      if (key.repeat || !["Enter", " ", "Tab", "ArrowDown", "ArrowUp", "PageDown", "PageUp"].includes(key.key)) return;
      recordJourneyInteraction("keyboard");
    } else if (event.type === "wheel" || event.type === "touchmove") {
      recordJourneyInteraction("scroll");
    } else if (event.type === "input") {
      if (performance.now() - lastEdit < 5000) return;
      lastEdit = performance.now();
      recordJourneyInteraction("edit");
    } else if (event.type === "submit") {
      recordJourneyInteraction("submit");
    } else {
      recordJourneyInteraction("click");
    }
  };
  document.addEventListener("visibilitychange", syncVisibility);
  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("pageshow", onPageShow);
  const interactionTypes = ["click", "submit", "wheel", "touchmove", "keydown", "input"];
  for (const type of interactionTypes) {
    document.addEventListener(type, onInteraction, { capture: true, passive: true });
  }
  syncVisibility();
  if (Capacitor.isNativePlatform()) {
    void App.addListener("appStateChange", ({ isActive }) => {
      if (disposed) return;
      nativeEventReceived = true;
      nativeActive = isActive;
      syncVisibility();
    }).then(handle => {
      if (disposed) return handle.remove();
      removeNative = () => handle.remove();
    }).catch(error => console.warn("[journey] native lifecycle listener failed", error));
    void App.getState().then(({ isActive }) => {
      if (disposed || nativeEventReceived) return;
      nativeActive = isActive;
      syncVisibility();
    }).catch(error => console.warn("[journey] native lifecycle state unavailable", error));
  }
  return () => {
    disposed = true;
    document.removeEventListener("visibilitychange", syncVisibility);
    window.removeEventListener("pagehide", onPageHide);
    window.removeEventListener("pageshow", onPageShow);
    for (const type of interactionTypes) document.removeEventListener(type, onInteraction, true);
    if (removeNative) void removeNative().catch(error => console.warn("[journey] native lifecycle cleanup failed", error));
  };
}
